// src/pages/children/Children.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Stack,
  Badge,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Alert,
  Skeleton,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Warning as WarningIcon,
  ContentCopy as ContentCopyIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  Groups as FamilyIcon,
  ExpandMore as ExpandMoreIcon,
  Merge as MergeIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  SchoolOutlined as GraduateIcon
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, where, doc, deleteDoc, updateDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

// Paleta de colores verde pastel
const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#7ED321',
  warning: '#F5A623',
  error: '#E74C3C',
  background: '#F8FDF8',
  surface: '#FFFFFF',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D'
  },
  status: {
    unique: '#27AE60',
    duplicate: '#E74C3C',
    warning: '#F39C12'
  }
};

const Children = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false);
  const [mergingChild, setMergingChild] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showBajas, setShowBajas] = useState(false);
  const [bajaTarget, setBajaTarget] = useState(null);
  const [bulkCurso, setBulkCurso] = useState('');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Función para normalizar nombres y detectar duplicados
  const normalizeString = useCallback((str) => {
    if (!str) return '';
    return str.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .trim();
  }, []);

  // Detectar duplicados usando una función de similitud más sofisticada
  const findPotentialDuplicates = useCallback((childrenList) => {
    const duplicateGroups = [];
    const processed = new Set();

    childrenList.forEach((child, index) => {
      if (processed.has(child.id)) return;

      const duplicates = [child];
      const childFirstName = normalizeString(child.nombre || '');
      const childLastName = normalizeString(child.apellidos || '');
      const childFullName = `${childFirstName} ${childLastName}`.trim();
      const childCourse = normalizeString(child.curso || '');
      const childClass = normalizeString(child.clase || '');

      childrenList.forEach((otherChild, otherIndex) => {
        if (index === otherIndex || processed.has(otherChild.id)) return;

        const otherFirstName = normalizeString(otherChild.nombre || '');
        const otherLastName = normalizeString(otherChild.apellidos || '');
        const otherFullName = `${otherFirstName} ${otherLastName}`.trim();
        const otherCourse = normalizeString(otherChild.curso || '');
        const otherClass = normalizeString(otherChild.clase || '');

        // Criterios de duplicación (más flexibles):
        let isDuplicate = false;

        // 1. Nombres completos exactamente iguales
        if (childFullName && otherFullName && childFullName === otherFullName) {
          isDuplicate = true;
        }
        
        // 2. Mismo nombre Y mismo apellido (por separado)
        else if (childFirstName && otherFirstName && childLastName && otherLastName &&
                 childFirstName === otherFirstName && childLastName === otherLastName) {
          isDuplicate = true;
        }
        
        // 3. Mismo nombre completo Y mismo curso/clase
        else if (childFullName && otherFullName && 
                 childFullName === otherFullName &&
                 childCourse && otherCourse && childCourse === otherCourse) {
          isDuplicate = true;
        }
        
        // 4. Nombres muy similares (diferencia mínima) Y mismo curso
        else if (childFullName && otherFullName && childCourse && otherCourse &&
                 childCourse === otherCourse &&
                 (childFullName.includes(otherFullName) || otherFullName.includes(childFullName))) {
          isDuplicate = true;
        }

        // 5. Exactamente el mismo nombre Y apellido Y curso Y clase
        else if (childFirstName && otherFirstName &&
                 childFirstName === otherFirstName &&
                 childLastName && otherLastName && childLastName === otherLastName &&
                 childCourse && otherCourse && childCourse === otherCourse &&
                 childClass && otherClass && childClass === otherClass) {
          isDuplicate = true;
        }

        if (isDuplicate) {
          duplicates.push(otherChild);
          processed.add(otherChild.id);
        }
      });

      if (duplicates.length > 1) {
        duplicateGroups.push(duplicates);
        duplicates.forEach(dup => processed.add(dup.id));
      }
    });

    return duplicateGroups;
  }, [normalizeString]);

  // Memoizar cálculos pesados
  const { filteredChildren, duplicateGroups, stats } = useMemo(() => {
    // Filtrar hijos
    const visibles = showBajas ? children : children.filter(child => child.baja !== true);

    const filtered = searchTerm ? 
      visibles.filter(child => {
        const searchLower = searchTerm.toLowerCase();
        return (child.nombre || '').toLowerCase().includes(searchLower) ||
               (child.apellidos || '').toLowerCase().includes(searchLower) ||
               (child.curso || '').toLowerCase().includes(searchLower) ||
               (child.clase || '').toLowerCase().includes(searchLower) ||
               (child.parent?.firstName || '').toLowerCase().includes(searchLower) ||
               (child.parent?.lastName || '').toLowerCase().includes(searchLower);
      }) : visibles;

    // Detectar duplicados
    const duplicates = findPotentialDuplicates(children);
    
    // Debug temporal - remover en producción
    console.log('Children data:', children.map(c => ({ 
      id: c.id, 
      nombre: c.nombre, 
      apellidos: c.apellidos, 
      curso: c.curso,
      normalized: `${normalizeString(c.nombre || '')} ${normalizeString(c.apellidos || '')}`.trim()
    })));
    console.log('Duplicate groups found:', duplicates);

    // Calcular estadísticas
    const totalChildren = children.length;
    const duplicatedChildren = duplicates.reduce((acc, group) => acc + group.length, 0);
    const uniqueChildren = totalChildren - duplicatedChildren;
    const coursesCount = new Set(children.map(child => child.curso).filter(Boolean)).size;
    const classesCount = new Set(children.map(child => `${child.curso}-${child.clase}`).filter(combo => combo !== '-')).size;

    return {
      filteredChildren: filtered,
      duplicateGroups: duplicates,
      stats: {
        total: totalChildren,
        unique: uniqueChildren,
        duplicated: duplicatedChildren,
        duplicateGroups: duplicates.length,
        courses: coursesCount,
        classes: classesCount
      }
    };
  }, [children, searchTerm, showBajas, findPotentialDuplicates, normalizeString]);

  // --- Bajas -------------------------------------------------------------
  // No se borra nada: se marca baja:true y se guarda la fecha. Asi el
  // historico de pedidos, inscripciones y contabilidad sigue cuadrando.

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Un padre solo se da de baja cuando TODOS sus hijos estan de baja.
  // Si le queda alguno en el cole, se deja activo.
  const syncParentsStatus = useCallback(async (parentIds, allChildren) => {
    const batch = writeBatch(db);
    let changes = 0;

    parentIds.forEach((parentId) => {
      if (!parentId) return;
      const siblings = allChildren.filter((c) => c.parentId === parentId);
      if (siblings.length === 0) return;
      const todosDeBaja = siblings.every((c) => c.baja === true);
      batch.update(doc(db, 'users', parentId), {
        baja: todosDeBaja,
        fechaBaja: todosDeBaja ? serverTimestamp() : null
      });
      changes += 1;
    });

    if (changes > 0) await batch.commit();
    return changes;
  }, []);

  const applyBaja = useCallback(async (targetChildren, baja) => {
    if (targetChildren.length === 0) return;
    setProcessing(true);
    try {
      const batch = writeBatch(db);
      targetChildren.forEach((child) => {
        batch.update(doc(db, 'children', child.id), {
          baja,
          fechaBaja: baja ? serverTimestamp() : null
        });
      });
      await batch.commit();

      // Estado local ya actualizado, para decidir el estado de los padres
      const updatedChildren = children.map((c) =>
        targetChildren.some((t) => t.id === c.id) ? { ...c, baja } : c
      );

      const parentIds = [...new Set(targetChildren.map((c) => c.parentId).filter(Boolean))];
      const parentsChanged = await syncParentsStatus(parentIds, updatedChildren);

      setChildren(updatedChildren);

      const accion = baja ? 'dado de baja' : 'reactivado';
      const sufijo = parentsChanged > 0 ? ` y se actualizaron ${parentsChanged} familia(s)` : '';
      showSnackbar(`${targetChildren.length} alumno(s) ${accion}${sufijo}`);
    } catch (error) {
      console.error('Error al actualizar bajas:', error);
      showSnackbar('No se pudo completar la operación', 'error');
    } finally {
      setProcessing(false);
      setBajaTarget(null);
      setBulkDialogOpen(false);
    }
  }, [children, syncParentsStatus, showSnackbar]);

  // Cursos disponibles para la baja masiva de fin de curso
  const cursosDisponibles = useMemo(
    () => [...new Set(children.filter((c) => c.baja !== true).map((c) => c.curso).filter(Boolean))].sort(),
    [children]
  );

  const bulkCandidates = useMemo(
    () => children.filter((c) => c.baja !== true && c.curso === bulkCurso),
    [children, bulkCurso]
  );

  const fetchChildren = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch children
      const childrenQuery = query(collection(db, 'children'), orderBy('nombre'));
      const childrenSnapshot = await getDocs(childrenQuery);
      
      // Fetch all users in parallel
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      // Create users map for faster lookup
      const usersMap = new Map();
      usersSnapshot.docs.forEach(doc => {
        usersMap.set(doc.id, { id: doc.id, ...doc.data() });
      });
      
      // Combine children with parent data
      const childrenData = childrenSnapshot.docs.map(doc => {
        const childData = doc.data();
        return {
          id: doc.id,
          ...childData,
          parent: usersMap.get(childData.parentId) || null
        };
      });
      
      setChildren(childrenData);
    } catch (error) {
      console.error('Error fetching children:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadChildren = async () => {
      if (isMounted) {
        await fetchChildren();
      }
    };
    
    loadChildren();
    
    return () => {
      isMounted = false;
    };
  }, [fetchChildren]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleViewChild = useCallback((id) => {
    navigate(`/admin/children/${id}`);
  }, [navigate]);

  const handleMergeChildren = async (primaryChild, duplicatesToRemove) => {
    setProcessing(true);
    try {
      // Actualizar el hijo principal con información combinada si es necesario
      const { parent, id, ...childFields } = primaryChild;
      const updatedData = {
        ...childFields,
        // Conservar la información más completa
        nombre: primaryChild.nombre || duplicatesToRemove[0]?.nombre,
        apellidos: primaryChild.apellidos || duplicatesToRemove[0]?.apellidos,
        curso: primaryChild.curso || duplicatesToRemove[0]?.curso,
        clase: primaryChild.clase || duplicatesToRemove[0]?.clase,
        fechaNacimiento: primaryChild.fechaNacimiento || duplicatesToRemove[0]?.fechaNacimiento,
        // Agregar nota sobre la consolidación
        consolidatedFrom: duplicatesToRemove.map(d => d.id),
        lastConsolidated: new Date()
      };

      // Actualizar el registro principal
      await updateDoc(doc(db, 'children', primaryChild.id), updatedData);

      // Eliminar los duplicados
      await Promise.all(
        duplicatesToRemove.map(duplicate => 
          deleteDoc(doc(db, 'children', duplicate.id))
        )
      );

      // Recargar los datos
      await fetchChildren();
      setMergingChild(null);
    } catch (error) {
      console.error('Error merging children:', error);
    } finally {
      setProcessing(false);
    }
  };

  const isDuplicate = useCallback((childId) => {
    return duplicateGroups.some(group => 
      group.some(child => child.id === childId)
    );
  }, [duplicateGroups]);

  const StatsCard = ({ title, value, subtitle, color, icon }) => (
    <Card sx={{ 
      height: '100%',
      background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
      border: `1px solid ${color}30`,
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 25px ${color}20`
      }
    }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            backgroundColor: `${color}20`,
            color: color 
          }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
              {value}
            </Typography>
            <Typography variant="body1" sx={{ color: COLORS.text.primary, fontWeight: 500 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const TableSkeleton = () => (
    <>
      {[...Array(rowsPerPage)].map((_, index) => (
        <TableRow key={index}>
          {[...Array(5)].map((_, cellIndex) => (
            <TableCell key={cellIndex}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  return (
    <Box sx={{ backgroundColor: COLORS.background, minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
          Gestión de Hijos
        </Typography>
        <Stack direction="row" spacing={2}>
          {duplicateGroups.length > 0 && (
            <Button
              startIcon={<WarningIcon />}
              onClick={() => setDuplicatesDialogOpen(true)}
              variant="contained"
              color="warning"
              sx={{
                backgroundColor: COLORS.warning,
                '&:hover': { backgroundColor: COLORS.warning + 'DD' }
              }}
            >
              {duplicateGroups.length} Duplicados
            </Button>
          )}
        </Stack>
      </Box>

      {/* Alerta de duplicados */}
      {duplicateGroups.length > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setDuplicatesDialogOpen(true)}
            >
              Revisar
            </Button>
          }
        >
          Se detectaron {stats.duplicated} hijos potencialmente duplicados en {duplicateGroups.length} grupos. 
          Se recomienda revisar y consolidar los registros.
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <StatsCard
            title="Total Hijos"
            value={stats.total}
            color={COLORS.accent}
            icon={<PersonIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatsCard
            title="Únicos"
            value={stats.unique}
            subtitle="Sin duplicados"
            color={COLORS.status.unique}
            icon={<CheckIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatsCard
            title="Duplicados"
            value={stats.duplicated}
            subtitle={`${stats.duplicateGroups} grupos`}
            color={COLORS.status.duplicate}
            icon={<ContentCopyIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatsCard
            title="Cursos"
            value={stats.courses}
            color={COLORS.secondary}
            icon={<SchoolIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatsCard
            title="Clases"
            value={stats.classes}
            color={COLORS.primary}
            icon={<ClassIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatsCard
            title="Familias"
            value={new Set(children.map(c => c.parentId).filter(Boolean)).size}
            color={COLORS.accent}
            icon={<FamilyIcon />}
          />
        </Grid>
      </Grid>

      {/* Search and Table */}
      <Paper sx={{ 
        backgroundColor: COLORS.surface,
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ p: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar por nombre, apellidos, curso, clase o padre..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: COLORS.background,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: COLORS.accent,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: COLORS.accent,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: COLORS.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', px: 3, pb: 2 }}>
          <FormControlLabel
            control={<Switch checked={showBajas} onChange={(e) => setShowBajas(e.target.checked)} />}
            label="Mostrar bajas"
          />
          <Button
            variant="outlined"
            startIcon={<GraduateIcon />}
            onClick={() => { setBulkCurso(''); setBulkDialogOpen(true); }}
            sx={{ color: COLORS.accent, borderColor: COLORS.accent }}
          >
            Dar de baja por curso
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: COLORS.background }}>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Nombre
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Curso
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Clase
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Padre/Madre
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Estado
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : filteredChildren.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm ? 'No se encontraron hijos que coincidan con la búsqueda' : 'No hay hijos registrados'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredChildren
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((child) => {
                    const isChildDuplicate = isDuplicate(child.id);
                    
                    return (
                      <TableRow 
                        key={child.id}
                        sx={{
                          opacity: child.baja === true ? 0.55 : 1,
                          backgroundColor: isChildDuplicate ? COLORS.status.duplicate + '05' : 'transparent',
                          '&:hover': {
                            backgroundColor: isChildDuplicate ? 
                              COLORS.status.duplicate + '10' : 
                              COLORS.primary + '10',
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ 
                              mr: 2, 
                              bgcolor: isChildDuplicate ? COLORS.status.duplicate : COLORS.accent,
                              width: 32,
                              height: 32
                            }}>
                              {child.nombre?.charAt(0) || '?'}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {child.nombre} {child.apellidos}
                              </Typography>
                              {child.fechaNacimiento && (
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(child.fechaNacimiento).toLocaleDateString('es-ES')}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={child.curso || 'No especificado'}
                            size="small"
                            sx={{
                              backgroundColor: child.curso ? COLORS.secondary + '20' : COLORS.text.secondary + '20',
                              color: child.curso ? COLORS.secondary : COLORS.text.secondary
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={child.clase || 'No especificada'}
                            size="small"
                            sx={{
                              backgroundColor: child.clase ? COLORS.primary + '20' : COLORS.text.secondary + '20',
                              color: child.clase ? COLORS.primary : COLORS.text.secondary
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {child.parent ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {child.parent.firstName} {child.parent.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {child.parent.email}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip 
                              label="Sin padre asociado" 
                              size="small"
                              sx={{
                                backgroundColor: COLORS.error + '20',
                                color: COLORS.error
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {child.baja === true ? (
                            <Chip
                              icon={<ArchiveIcon fontSize="small" />}
                              label="Baja"
                              size="small"
                              sx={{
                                backgroundColor: COLORS.text.secondary + '20',
                                color: COLORS.text.secondary,
                                fontWeight: 'bold'
                              }}
                            />
                          ) : isChildDuplicate ? (
                            <Chip 
                              icon={<WarningIcon fontSize="small" />}
                              label="Posible duplicado"
                              size="small"
                              sx={{
                                backgroundColor: COLORS.status.duplicate + '20',
                                color: COLORS.status.duplicate,
                                fontWeight: 'bold'
                              }}
                            />
                          ) : (
                            <Chip 
                              icon={<CheckIcon fontSize="small" />}
                              label="Único"
                              size="small"
                              sx={{
                                backgroundColor: COLORS.status.unique + '20',
                                color: COLORS.status.unique
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Ver detalles">
                            <IconButton 
                              onClick={() => handleViewChild(child.id)}
                              sx={{ 
                                color: COLORS.accent,
                                '&:hover': { backgroundColor: COLORS.accent + '20' }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {child.baja === true ? (
                            <Tooltip title="Reactivar alumno">
                              <IconButton
                                onClick={() => applyBaja([child], false)}
                                disabled={processing}
                                sx={{ color: COLORS.success }}
                              >
                                <UnarchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Dar de baja (pasa al instituto)">
                              <IconButton
                                onClick={() => setBajaTarget(child)}
                                disabled={processing}
                                sx={{ color: COLORS.warning }}
                              >
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredChildren.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
          sx={{
            borderTop: `1px solid ${COLORS.primary}30`,
            backgroundColor: COLORS.background
          }}
        />
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          backgroundColor: COLORS.accent,
          '&:hover': { backgroundColor: COLORS.secondary }
        }}
        onClick={() => navigate('/admin/children/create')}
      >
        <PersonAddIcon />
      </Fab>

      {/* Duplicates Dialog */}
      <Dialog
        open={duplicatesDialogOpen}
        onClose={() => setDuplicatesDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ color: COLORS.text.primary, fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningIcon sx={{ color: COLORS.warning }} />
            Revisar Hijos Duplicados
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Se detectaron {duplicateGroups.length} grupos de posibles duplicados. 
            Revisa cada grupo y decide si consolidar los registros.
          </Typography>
          
          {duplicateGroups.map((group, groupIndex) => (
            <Accordion key={groupIndex} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Chip 
                    label={`Grupo ${groupIndex + 1}`}
                    size="small"
                    sx={{ backgroundColor: COLORS.warning + '20', color: COLORS.warning }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {group[0]?.nombre} {group[0]?.apellidos}
                  </Typography>
                  <Chip 
                    label={`${group.length} registros`}
                    size="small"
                    color="error"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {group.map((duplicate, index) => (
                    <Grid item xs={12} md={6} key={duplicate.id}>
                      <Card 
                        variant="outlined"
                        sx={{ 
                          borderColor: index === 0 ? COLORS.status.unique : COLORS.status.duplicate,
                          backgroundColor: index === 0 ? COLORS.status.unique + '05' : COLORS.status.duplicate + '05'
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: index === 0 ? COLORS.status.unique : COLORS.status.duplicate }}>
                              {duplicate.nombre?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="h6">
                                {duplicate.nombre} {duplicate.apellidos}
                              </Typography>
                              {index === 0 && (
                                <Chip 
                                  label="Registro principal sugerido"
                                  size="small"
                                  sx={{ backgroundColor: COLORS.status.unique + '20', color: COLORS.status.unique }}
                                />
                              )}
                            </Box>
                          </Box>
                          
                          <List dense>
                            <ListItem>
                              <ListItemText 
                                primary="Curso/Clase"
                                secondary={`${duplicate.curso || 'N/A'} - ${duplicate.clase || 'N/A'}`}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Padre/Madre"
                                secondary={duplicate.parent ? 
                                  `${duplicate.parent.firstName} ${duplicate.parent.lastName}` : 
                                  'Sin padre asociado'
                                }
                              />
                            </ListItem>
                            {duplicate.fechaNacimiento && (
                              <ListItem>
                                <ListItemText 
                                  primary="Fecha de nacimiento"
                                  secondary={new Date(duplicate.fechaNacimiento).toLocaleDateString('es-ES')}
                                />
                              </ListItem>
                            )}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    startIcon={<MergeIcon />}
                    onClick={() => {
                      setMergingChild({
                        primary: group[0],
                        duplicates: group.slice(1),
                        groupIndex
                      });
                    }}
                    variant="contained"
                    disabled={processing}
                    sx={{
                      backgroundColor: COLORS.accent,
                      '&:hover': { backgroundColor: COLORS.secondary }
                    }}
                  >
                    Consolidar registros
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDuplicatesDialogOpen(false)}
            sx={{ color: COLORS.text.secondary }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Merge Confirmation Dialog */}
      <Dialog
        open={!!mergingChild}
        onClose={() => setMergingChild(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ color: COLORS.text.primary, fontWeight: 'bold' }}>
          Confirmar Consolidación
        </DialogTitle>
        <DialogContent>
          {mergingChild && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                ¿Estás seguro de que quieres consolidar estos registros?
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                Se mantendrá el registro de <strong>{mergingChild.primary?.nombre} {mergingChild.primary?.apellidos}</strong> 
                y se eliminarán {mergingChild.duplicates?.length} registro(s) duplicado(s).
              </Alert>
              
              <Typography variant="body2" color="text.secondary">
                Los registros eliminados se guardarán como referencia en el registro principal.
                Esta acción no se puede deshacer.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setMergingChild(null)} 
            disabled={processing}
            sx={{ color: COLORS.text.secondary }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={() => mergingChild && handleMergeChildren(mergingChild.primary, mergingChild.duplicates)}
            variant="contained"
            disabled={processing}
            sx={{
              backgroundColor: COLORS.accent,
              '&:hover': { backgroundColor: COLORS.secondary },
              '&:disabled': { backgroundColor: COLORS.text.secondary }
            }}
          >
            {processing ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              'Confirmar consolidación'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirmar baja individual */}
      <Dialog open={Boolean(bajaTarget)} onClose={() => setBajaTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Dar de baja</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Dar de baja a <strong>{bajaTarget?.nombre} {bajaTarget?.apellidos}</strong>?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            No se borra nada. El alumno deja de aparecer en los listados, pero su historial de
            pedidos e inscripciones se conserva. Puedes reactivarlo cuando quieras.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBajaTarget(null)} disabled={processing}>Cancelar</Button>
          <Button
            onClick={() => applyBaja([bajaTarget], true)}
            variant="contained"
            color="warning"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} /> : <ArchiveIcon />}
          >
            Dar de baja
          </Button>
        </DialogActions>
      </Dialog>

      {/* Baja masiva por curso */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dar de baja un curso entero</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Para final de curso: selecciona el curso que pasa al instituto.
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Curso</InputLabel>
            <Select value={bulkCurso} label="Curso" onChange={(e) => setBulkCurso(e.target.value)}>
              {cursosDisponibles.map((curso) => (
                <MenuItem key={curso} value={curso}>{curso}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {bulkCurso && (
            <Alert severity={bulkCandidates.length > 0 ? 'warning' : 'info'} sx={{ mt: 2 }}>
              {bulkCandidates.length > 0
                ? `Se darán de baja ${bulkCandidates.length} alumno(s) de ${bulkCurso}. Las familias que no tengan ningún otro hijo en el colegio se darán de baja también.`
                : `No hay alumnos activos en ${bulkCurso}.`}
            </Alert>
          )}

          {bulkCandidates.length > 0 && (
            <List dense sx={{ mt: 1, maxHeight: 220, overflow: 'auto' }}>
              {bulkCandidates.map((child) => (
                <ListItem key={child.id}>
                  <ListItemText
                    primary={`${child.nombre || ''} ${child.apellidos || ''}`.trim()}
                    secondary={child.clase ? `Clase ${child.clase}` : null}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)} disabled={processing}>Cancelar</Button>
          <Button
            onClick={() => applyBaja(bulkCandidates, true)}
            variant="contained"
            color="warning"
            disabled={processing || bulkCandidates.length === 0}
            startIcon={processing ? <CircularProgress size={16} /> : <ArchiveIcon />}
          >
            Dar de baja ({bulkCandidates.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default Children;